import { useState, type DragEvent } from 'react';
import { Permissao, type ProcessoCartaoDto } from '@nexital/shared';
import { useEnvio, useQuadro } from '../../application/dependencias';
import { juntarArquivos } from '../shell/DialogoEnviarArquivos';
import { ARRASTE_ARQUIVO } from './ArquivosSemProcesso';

const temArquivos = (e: DragEvent) => e.dataTransfer.types.includes('Files');

/**
 * Soltar sobre um cartão (ADR-0020): arquivos do computador vão direto para aquele processo; arquivo da bandeja
 * "Arquivos sem processo" é vinculado a ele. `realce` marca o cartão enquanto algo passa por cima.
 */
export function useSolturaNoCartao(processo: ProcessoCartaoDto) {
  const pode = useQuadro((s) => s.dados?.permissoes.includes(Permissao.EnviarDocumentos) ?? false);
  const enviar = useEnvio((s) => s.enviarAoProcesso);
  const agir = useEnvio((s) => s.agir);
  const [realce, definirRealce] = useState(false);
  const aceita = (e: DragEvent) => pode && (temArquivos(e) || e.dataTransfer.types.includes(ARRASTE_ARQUIVO));
  const alvo = { uuid: processo.uuid, numero: processo.numero };
  return {
    realce,
    props: {
      onDragOver: (e: DragEvent) => {
        if (!aceita(e)) return;
        e.preventDefault();
        e.stopPropagation();
        definirRealce(true);
      },
      onDragLeave: () => definirRealce(false),
      onDrop: (e: DragEvent) => {
        if (!aceita(e)) return;
        e.preventDefault();
        e.stopPropagation();
        definirRealce(false);
        const arquivoUuid = e.dataTransfer.getData(ARRASTE_ARQUIVO);
        if (arquivoUuid) return void agir(arquivoUuid, 'vincular', alvo);
        const arquivos = juntarArquivos([], e.dataTransfer.files);
        if (arquivos.length) void enviar(arquivos, alvo);
      },
    },
  };
}

type Receptor = { onDragOver: (e: DragEvent) => void; onDrop: (e: DragEvent) => void };

/**
 * Props do elemento do quadro: arquivos do computador soltos num lugar vazio abrem o "Enviar arquivos" com eles;
 * o resto (cartões da bandeja Sem estado) segue para o receptor de antes.
 */
export function useSolturaNoQuadro(daBandeja: Receptor): Receptor {
  const pode = useQuadro((s) => s.dados?.permissoes.includes(Permissao.EnviarDocumentos) ?? false);
  const abrir = useEnvio((s) => s.abrirDialogo);
  return {
    onDragOver: (e: DragEvent) => {
      if (pode && temArquivos(e)) return e.preventDefault();
      daBandeja.onDragOver(e);
    },
    onDrop: (e: DragEvent) => {
      if (!pode || !temArquivos(e)) return daBandeja.onDrop(e);
      e.preventDefault();
      abrir(Array.from(e.dataTransfer.files));
    },
  };
}
